import momentModule from "moment";
import { USAGE_DEFAULT_MODES } from "../constants";
import { isValueNullish } from "./valueUtilities";

const moment = ((momentModule as typeof momentModule & { default?: typeof momentModule }).default ?? momentModule);

export interface UsageStatistics {
	Count: number;
	LastUsed: string;
}

export interface UsageDataPoint {
	Id: number;
	Usage?: UsageStatistics;
	IsActive?: boolean;
	IsRecent?: boolean;
	DeactivatedAt?: string | null;
}

export interface UsageEntity extends UsageDataPoint {
	Entities?: Array<{ Entity?: UsageEntity | null }>;
}

interface UsageDefaultField {
	IsRecentlyUsedEnabled?: boolean;
	UsageDefaultMode?: string;
}

export interface UsageUpdate {
	FieldId?: number;
	DataId?: number | null;
	EntityId?: number | null;
	IsDeleted?: boolean;
}

type UsageScore = number | momentModule.Moment;

const getUpdateId = (value: UsageUpdate) => value?.EntityId ?? value?.DataId;

const getUpdateKey = (value: UsageUpdate) => {
	const id = getUpdateId(value);

	if(isValueNullish(value?.FieldId) || isValueNullish(id)) {
		return undefined;
	}

	const valueType = !isValueNullish(value.EntityId) ? "entity" : "data";

	return `${value.FieldId}:${valueType}:${id}`;
};

function isUsageEntity(dataPoint: UsageDataPoint): dataPoint is UsageEntity {
	return "Entities" in dataPoint;
}

function getNestedEntities(dataPoint: UsageDataPoint) {
	if(!isUsageEntity(dataPoint)) {
		return [];
	}

	return (dataPoint.Entities ?? [])
		.map(entity => entity.Entity)
		.filter((entity): entity is UsageEntity => !isValueNullish(entity));
}

function visitDataPoints(data: UsageDataPoint[] | undefined, visitor: (dataPoint: UsageDataPoint) => void) {
	for(const dataPoint of data ?? []) {
		visitor(dataPoint);
		visitDataPoints(getNestedEntities(dataPoint), visitor);
	}
}

function isSelectableUsageCandidate(dataPoint: UsageDataPoint) {
	const deactivatedAt = moment(dataPoint?.DeactivatedAt, moment.ISO_8601, true);

	return !isValueNullish(dataPoint?.Usage)
		&& dataPoint.IsActive !== false
		&& (isValueNullish(dataPoint.DeactivatedAt) || deactivatedAt.isAfter(moment()));
}

function getUsageScore(dataPoint: UsageDataPoint, mode: string): UsageScore | undefined {
	if(!isSelectableUsageCandidate(dataPoint)) {
		return undefined;
	}

	if(mode === USAGE_DEFAULT_MODES.MostOftenUsed) {
		const count = Number(dataPoint.Usage?.Count);

		return Number.isInteger(count) ? count : undefined;
	}

	if(mode === USAGE_DEFAULT_MODES.MostRecentlyUsed) {
		const lastUsed = moment(dataPoint.Usage?.LastUsed, moment.ISO_8601, true);

		return lastUsed.isValid() ? lastUsed : undefined;
	}

	return undefined;
}

function isHigherUsageScore(score: UsageScore, selectedScore: UsageScore | undefined) {
	if(selectedScore === undefined) {
		return true;
	}

	return typeof score === "number"
		? typeof selectedScore === "number" && score > selectedScore
		: moment.isMoment(selectedScore) && score.isAfter(selectedScore);
}

export function getUsageDefault(data: UsageDataPoint[] | undefined, mode: string | undefined) {
	if(mode !== USAGE_DEFAULT_MODES.MostRecentlyUsed && mode !== USAGE_DEFAULT_MODES.MostOftenUsed) {
		return undefined;
	}

	let selected: UsageDataPoint | undefined;
	let selectedScore: UsageScore | undefined;

	visitDataPoints(data, dataPoint => {
		const score = getUsageScore(dataPoint, mode);

		if(score !== undefined && isHigherUsageScore(score, selectedScore)) {
			selected = dataPoint;
			selectedScore = score;
		}
	});

	return selected;
}

export function getFieldUsageDefault(field: UsageDefaultField, data: UsageDataPoint[] | undefined) {
	return field.IsRecentlyUsedEnabled === true && !isValueNullish(field.UsageDefaultMode)
		? getUsageDefault(data, field.UsageDefaultMode)
		: undefined;
}

export function updateUsage(data: UsageDataPoint[] | undefined, id: number, usedAt = moment().toISOString()) {
	for(const dataPoint of data ?? []) {
		if(dataPoint.Id === id) {
			const currentCount = Number(dataPoint.Usage?.Count);

			dataPoint.IsRecent = true;
			dataPoint.Usage = {
				Count: Number.isInteger(currentCount) ? currentCount + 1 : 1,
				LastUsed: moment(usedAt).toISOString()
			};

			return true;
		}

		if(updateUsage(getNestedEntities(dataPoint), id, usedAt)) {
			dataPoint.IsRecent = true;
			return true;
		}
	}

	return false;
}

export function snapshotUsageUpdates(updates: UsageUpdate[] | undefined): UsageUpdate[] {
	return (updates ?? []).map(({
		FieldId,
		DataId,
		EntityId,
		IsDeleted
	}) => ({
		FieldId,
		DataId,
		EntityId,
		IsDeleted
	}));
}

export function getNewUsageUpdates(updates: UsageUpdate[] | undefined, previousValues: UsageUpdate[] | undefined) {
	const previousKeys = new Set(
		(previousValues ?? [])
			.filter(value => value.IsDeleted !== true)
			.map(getUpdateKey)
			.filter((key): key is string => key !== undefined)
	);
	const includedKeys = new Set<string>();

	return (updates ?? []).filter(value => {
		if(value.IsDeleted === true) {
			return false;
		}

		const key = getUpdateKey(value);

		if(key === undefined || previousKeys.has(key) || includedKeys.has(key)) {
			return false;
		}

		includedKeys.add(key);
		return true;
	});
}
